import { TrackerSound, decodeSamples, encodeSamples, newTrackerId } from "./tracker";

// makes sounds. the snes cannot synthesize, so everything here ends up as a short recording:
// a "tone" is one tiny cycle that loops, so it plays any key and costs almost no memory,
// an "effect" is a one shot (coin, kick, explosion) that plays once at the pitch it was made in

export type Shape = "square" | "triangle" | "saw" | "sine" | "noise";
export type SoundKind = "tone" | "effect";

export interface SynthParams {
  kind: SoundKind;
  shape: Shape;
  // effects: the starting pitch in hz
  freq: number;
  // effects: how far the pitch moves over the sound, -100 to 100 (about three octaves each way)
  slide: number;
  // effects: milliseconds
  length: number;
  // 0 holds the volume, 100 dies away fast
  fade: number;
  // 0 clean, 100 very rough
  crunch: number;
  // squares: share of each cycle that is high
  duty: number;
  // 0 sharp, 100 dull
  smooth: number;
  seed: number;
}

export const SHAPES: Array<{ id: Shape; label: string }> = [
  { id: "square", label: "Square" },
  { id: "triangle", label: "Triangle" },
  { id: "saw", label: "Saw" },
  { id: "sine", label: "Sine" },
  { id: "noise", label: "Noise" }
];

export const EFFECT_RATE = 16000;
export const TONE_CYCLE = 32;
const NOISE_CYCLE = 1024;
const PEAK = 110;
// the pitch the C-5 key plays
const C5_HZ = 261.63;

export const DEFAULT_PARAMS: SynthParams = { kind: "effect", shape: "square", freq: 440, slide: 0, length: 250, fade: 70, crunch: 0, duty: 0.5, smooth: 0, seed: 1 };

function random(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function wave(shape: Shape, phase: number, duty: number): number {
  if (shape === "square") return phase < duty ? 1 : -1;
  if (shape === "triangle") return phase < 0.5 ? phase * 4 - 1 : 3 - phase * 4;
  if (shape === "saw") return phase * 2 - 1;
  return Math.sin(phase * Math.PI * 2);
}

export interface Made {
  data: Int8Array;
  rate: number;
  loop: boolean;
}

export function synthesize(params: SynthParams): Made {
  const next = random(params.seed);
  const crunch = Math.max(0, Math.min(100, params.crunch)) / 100;
  const steps = Math.max(2, Math.round(32 * (1 - crunch)));
  const smooth = Math.max(0, Math.min(100, params.smooth)) / 100;
  if (params.kind === "tone") {
    const length = params.shape === "noise" ? NOISE_CYCLE : TONE_CYCLE;
    const raw = new Float32Array(length);
    for (let i = 0; i < length; i++) raw[i] = params.shape === "noise" ? next() * 2 - 1 : wave(params.shape, i / length, params.duty);
    return { data: toInt8(raw, steps, smooth), rate: Math.round(length * C5_HZ), loop: true };
  }
  const length = Math.max(64, Math.round((Math.max(5, Math.min(1500, params.length)) / 1000) * EFFECT_RATE));
  const raw = new Float32Array(length);
  let phase = 0;
  let held = next() * 2 - 1;
  for (let i = 0; i < length; i++) {
    const u = i / length;
    const hz = params.freq * 2 ** ((params.slide / 100) * 3 * u);
    phase += hz / EFFECT_RATE;
    if (phase >= 1) {
      phase -= Math.floor(phase);
      held = next() * 2 - 1;
    }
    const value = params.shape === "noise" ? held : wave(params.shape, phase, params.duty);
    const envelope = (1 - u) ** ((Math.max(0, Math.min(100, params.fade)) / 100) * 3);
    raw[i] = value * envelope;
  }
  // a last few samples of fade so a one shot never ends on a click
  for (let i = 0; i < Math.min(32, length); i++) raw[length - 1 - i] *= i / 32;
  return { data: toInt8(raw, steps, smooth), rate: EFFECT_RATE, loop: false };
}

// smoothing, rounding to fewer levels (the crunch), and scaling to a safe peak
function toInt8(raw: Float32Array, levels: number, smooth: number): Int8Array {
  const out = new Int8Array(raw.length);
  let previous = 0;
  let peak = 0;
  const filtered = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    previous += (raw[i] - previous) * (1 - smooth * 0.92);
    filtered[i] = previous;
    peak = Math.max(peak, Math.abs(previous));
  }
  const scale = peak > 0 ? 1 / peak : 1;
  for (let i = 0; i < raw.length; i++) {
    const value = Math.round(((filtered[i] * scale + 1) / 2) * (levels - 1)) / (levels - 1) * 2 - 1;
    out[i] = Math.round(value * PEAK);
  }
  return out;
}

export interface ReadyMade {
  id: string;
  name: string;
  kind: SoundKind;
  hint: string;
  params: Partial<SynthParams>;
}

// the starter sounds: tones for tunes first, then the one shots
export const READY_MADE: ReadyMade[] = [
  { id: "lead", name: "Square lead", kind: "tone", hint: "bright, for melodies", params: { kind: "tone", shape: "square", duty: 0.5 } },
  { id: "pulse", name: "Thin pulse", kind: "tone", hint: "nasal, for melodies", params: { kind: "tone", shape: "square", duty: 0.25 } },
  { id: "bass", name: "Triangle bass", kind: "tone", hint: "soft and low", params: { kind: "tone", shape: "triangle" } },
  { id: "saw", name: "Saw", kind: "tone", hint: "buzzy, for chords", params: { kind: "tone", shape: "saw" } },
  { id: "sine", name: "Sine", kind: "tone", hint: "pure and smooth", params: { kind: "tone", shape: "sine" } },
  { id: "hiss", name: "Noise loop", kind: "tone", hint: "wind and static", params: { kind: "tone", shape: "noise", smooth: 30 } },
  { id: "kick", name: "Kick", kind: "effect", hint: "drum", params: { shape: "sine", freq: 160, slide: -85, length: 220, fade: 80 } },
  { id: "snare", name: "Snare", kind: "effect", hint: "drum", params: { shape: "noise", freq: 6000, length: 180, fade: 85, smooth: 20 } },
  { id: "hat", name: "Hat", kind: "effect", hint: "drum", params: { shape: "noise", freq: 12000, length: 70, fade: 95 } },
  { id: "coin", name: "Coin", kind: "effect", hint: "pickup", params: { shape: "square", freq: 700, slide: 45, length: 220, fade: 60 } },
  { id: "jump", name: "Jump", kind: "effect", hint: "hop", params: { shape: "square", freq: 220, slide: 70, length: 250, fade: 50 } },
  { id: "hit", name: "Hit", kind: "effect", hint: "thud", params: { shape: "noise", freq: 4000, slide: -40, length: 120, fade: 85, smooth: 40 } },
  { id: "laser", name: "Laser", kind: "effect", hint: "zap", params: { shape: "saw", freq: 1600, slide: -85, length: 200, fade: 50 } },
  { id: "boom", name: "Explosion", kind: "effect", hint: "big bang", params: { shape: "noise", freq: 900, slide: -60, length: 650, fade: 90, smooth: 70, crunch: 20 } },
  { id: "bleep", name: "Bleep", kind: "effect", hint: "menu beep", params: { shape: "triangle", freq: 880, length: 120, fade: 40 } }
];

export function paramsFor(preset: ReadyMade): SynthParams {
  return { ...DEFAULT_PARAMS, kind: preset.kind, ...preset.params };
}

export function soundFromMade(name: string, made: Made, volume = 64): TrackerSound {
  return {
    id: newTrackerId(),
    name: name.slice(0, 24),
    rate: made.rate,
    data: encodeSamples(made.data),
    trimStart: 0,
    trimEnd: made.data.length,
    loop: made.loop,
    loopStart: 0,
    loopEnd: made.data.length,
    volume
  };
}

export function readyMadeSound(preset: ReadyMade): TrackerSound {
  return soundFromMade(preset.name, synthesize(paramsFor(preset)));
}

// a random sound that is still usable: picks a starting point and nudges it
export function surprise(seed: number): SynthParams {
  const next = random(seed);
  const effects = READY_MADE.filter((preset) => preset.kind === "effect");
  const base = paramsFor(effects[Math.floor(next() * effects.length)]);
  return {
    ...base,
    seed: Math.floor(next() * 100000),
    freq: Math.round(base.freq * (0.6 + next() * 0.9)),
    slide: Math.max(-100, Math.min(100, Math.round(base.slide + (next() - 0.5) * 80))),
    length: Math.round(Math.max(40, base.length * (0.6 + next() * 0.9))),
    crunch: next() < 0.3 ? Math.round(next() * 60) : base.crunch
  };
}

// --- importing recordings

// to mono 8 bit at the chosen rate. averaging while going down keeps it from aliasing, and the loudest part is
// brought up to a safe peak so quiet recordings are not lost in 8 bits
export function toEightBit(input: Float32Array, fromRate: number, toRate: number): Int8Array {
  const length = Math.max(1, Math.floor((input.length * toRate) / fromRate));
  const out = new Float32Array(length);
  const ratio = fromRate / toRate;
  for (let i = 0; i < length; i++) {
    const start = i * ratio;
    const end = Math.min(input.length, Math.max(start + 1, (i + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let at = Math.floor(start); at < end; at++) {
      sum += input[at];
      count++;
    }
    out[i] = count ? sum / count : 0;
  }
  let peak = 0;
  for (const value of out) peak = Math.max(peak, Math.abs(value));
  const gain = peak > 0.001 ? 120 / 128 / peak : 1;
  return Int8Array.from(out, (value) => Math.max(-127, Math.round(value * gain * 128)));
}

// the same recording at a lower rate, from the 8 bit data already stored
export function lowerRate(sound: TrackerSound, toRate: number): TrackerSound {
  if (toRate >= sound.rate) return sound;
  const source = decodeSamples(sound.data);
  const data = toEightBit(Float32Array.from(source, (value) => value / 128), sound.rate, toRate);
  const scale = data.length / source.length;
  return {
    ...sound,
    rate: toRate,
    data: encodeSamples(data),
    trimStart: Math.round(sound.trimStart * scale),
    trimEnd: Math.max(1, Math.min(data.length, Math.round(sound.trimEnd * scale))),
    loopStart: Math.round(sound.loopStart * scale),
    loopEnd: Math.max(1, Math.min(data.length, Math.round(sound.loopEnd * scale)))
  };
}
